import { useGetBriefScripting } from "@entities/brief/lib/hooks/scripting/useGetScripting";
import { useUploadScripting } from "@entities/brief/lib/hooks/scripting/useUploadScripting";
import { useScriptConceptComments } from "@features/ScriptConceptEditor";
import { Divider, Layout, Modal } from "antd";
import { useEffect, useRef, useState } from "react";
import { ScriptBulkAttachFooter } from "./ScriptBulkAttachFooter";
import { ScriptBulkAttachHeader } from "./ScriptBulkAttachHeader";
import ScriptComments from "./ScriptComments";
import { ScriptFooter } from "./ScriptFooter";
import ScriptHeader from "./ScriptHeader";
import { ScriptPageHeader } from "./ScriptPageHeader";
import { UploadScriptModal } from "./UploadScriptModal";

import { useUploadTaskContent } from "@entities/campaigns/lib/hooks/useUploadTaskContent";
import { ToastService } from "@shared/lib/services";
import { Content } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ArrowDown } from "lucide-react";
import styles from "./styles.module.scss";

export function CampaignScriptPage({
  scriptId,
  onClose,
  refetchInfluencer = () => {},
  mode = "default",
  onBack,
  onAttach,
  isAttaching,
  attachCount = 0,
}: {
  scriptId: string;
  onClose: () => void;
  refetchInfluencer?: () => void;
  mode?: "default" | "bulkAttach";
  onBack?: () => void;
  onAttach?: () => void;
  isAttaching?: boolean;
  attachCount?: number;
}) {
  const {
    scripting: serverScript,
    isFetching,
    refetch,
  } = useGetBriefScripting(scriptId || "");

  const briefId = serverScript?.script?.briefId || "";

  const {
    uploadTaskContent,
    isUploaded: isUploadedScript,
    isUploading,
    variables,
  } = useUploadTaskContent(briefId, refetch);

  const reFetchData = () => {
    refetch();
    refetchInfluencer();
  };

  const [isUploadModalShown, setIsUploadModalShown] = useState(false);

  const {
    uploadScripting,
    isUploading: isUploadingScript,
    isUploaded,
  } = useUploadScripting(scriptId || "");

  useEffect(() => {
    if (isUploaded) {
      reFetchData();
      ToastService.success("Script uploaded successfully");
      setIsUploadModalShown(false);
    }
  }, [isUploaded]);

  const [isDownloading, setisDownloading] = useState(false);

  const conceptComments = useScriptConceptComments(
    serverScript?.script?.comments,
    serverScript?.script?._id || ""
  );

  useEffect(() => {
    if (isUploadedScript) {
      refetch();
      ToastService.success("Script submitted successfully");
    }
  }, [isUploadedScript]);

  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20; // 20px threshold
      setShowScrollIndicator(isScrollable && !isAtBottom);
    }
  };

  useEffect(() => {
    // Need to get the DOM element from the ref.
    const scrollableElement = contentRef.current;
    if (scrollableElement) {
      checkScroll();
      scrollableElement.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);

      return () => {
        scrollableElement.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [serverScript]); // Re-check when content changes

  const handleScrollClick = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const exportRef = useRef<HTMLDivElement | null>(null);

  const handleDownloadPdf = async () => {
    setisDownloading(true);

    setTimeout(async () => {
      if (!exportRef.current) return;
      const root = exportRef.current;
      root.classList.add(styles.pdfRoot);

      try {
        const canvas = await html2canvas(root, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: root.scrollWidth,
          windowHeight: root.scrollHeight,
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`script.pdf`);
      } finally {
        root.classList.remove(styles.pdfRoot);
        setisDownloading(false);
      }
    }, 1000);
  };

  if (isFetching || !serverScript?.script) {
    return (
      <Layout className={styles.layout}>
        {/* Header Shimmer */}
        <div className={styles.shimmerHeader}>
          <div className={styles.shimmerHeaderContent}>
            <div className={styles.shimmerProfile}>
              <div className={styles.shimmerAvatar}></div>
              <div className={styles.shimmerProfileText}>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "120px", marginBottom: "8px" }}
                ></div>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "150px" }}
                ></div>
              </div>
            </div>
            <div className={styles.shimmerHeaderActions}>
              <div className={styles.shimmerButton}></div>
              <div className={styles.shimmerButton}></div>
            </div>
          </div>
        </div>

        <Layout className={styles.innerLayout}>
          <Content className={styles.layoutContent}>
            {/* Concept Card Shimmer */}
            <div className={styles.shimmerCard}>
              <div className={styles.shimmerCardHeader}>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "100px" }}
                ></div>
                <div className={styles.shimmerIcon}></div>
              </div>
              <div className={styles.shimmerCardContent}>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "100%", marginBottom: "8px" }}
                ></div>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "100%", marginBottom: "8px" }}
                ></div>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "80%" }}
                ></div>
              </div>
            </div>

            {/* Script Blocks Shimmer */}
            <div className={styles.shimmerCard} style={{ marginTop: "24px" }}>
              <div className={styles.shimmerCardHeader}>
                <div
                  className={styles.shimmerLine}
                  style={{ width: "80px" }}
                ></div>
              </div>
              <div className={styles.shimmerBlocks}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className={styles.shimmerBlock}>
                    <div className={styles.shimmerBlockHeader}>
                      <div
                        className={styles.shimmerLine}
                        style={{ width: "120px" }}
                      ></div>
                      <div
                        className={styles.shimmerIcon}
                        style={{ width: "16px", height: "16px" }}
                      ></div>
                    </div>
                    <div className={styles.shimmerBlockContent}>
                      <div
                        className={styles.shimmerLine}
                        style={{ width: "100%", marginBottom: "6px" }}
                      ></div>
                      <div
                        className={styles.shimmerLine}
                        style={{ width: "90%", marginBottom: "6px" }}
                      ></div>
                      <div
                        className={styles.shimmerLine}
                        style={{ width: "75%" }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Content>
          <Divider type="vertical" style={{ height: "100%" }} />
          <Sider width={"35%"} style={{ background: "white" }}>
            <div className={styles.shimmerSidebar}>
              <div
                className={styles.shimmerLine}
                style={{ width: "100px", marginBottom: "16px" }}
              ></div>
              {[1, 2].map((i) => (
                <div key={i} className={styles.shimmerComment}>
                  <div
                    className={styles.shimmerLine}
                    style={{ width: "80%", marginBottom: "8px" }}
                  ></div>
                  <div
                    className={styles.shimmerLine}
                    style={{ width: "60%" }}
                  ></div>
                </div>
              ))}
            </div>
          </Sider>
        </Layout>
      </Layout>
    );
  }

  const script = serverScript?.script;

  const handleUploadScript = () => {
    uploadTaskContent({
      data: {
        campaignInfluencerId: serverScript?.script?.campaignInfluencerId || "",
        deliverableConfigRefId:
          serverScript?.campaignInfluencerDeliverableState?._id || "",
        taskId: serverScript?.campaignInfluencerTaskState?._id || "",
        contentUrls: [],
      },
    });
  };

  return (
    <Layout className={styles.layout}>
      {script && (
        <>
          {mode === "bulkAttach" ? (
            <ScriptBulkAttachHeader
              onBack={onBack || onClose}
              onClose={onClose}
            />
          ) : (
            <ScriptPageHeader
              serverScript={serverScript}
              refetch={reFetchData}
              onClose={onClose}
            />
          )}
          <Layout className={styles.innerLayout}>
            <div className={styles.editorPane}>
              <Content
                className={styles.layoutContent}
                ref={contentRef}
                onScroll={checkScroll}
              >
                <div ref={exportRef}>
                  <ScriptHeader
                    script={script}
                    isDownloading={isDownloading}
                    editorRef={conceptComments.editorRef}
                    commentsConfig={conceptComments.commentsConfig}
                    isSaving={conceptComments.isSaving}
                    savedFlash={conceptComments.savedFlash}
                    onEditorChange={conceptComments.scheduleSave}
                    onEditorReady={conceptComments.restoreExistingMarks}
                    resetSnapshot={conceptComments.resetSnapshot}
                    onMediaUploadStart={conceptComments.pauseAutosaveForMedia}
                    onMediaUploadComplete={conceptComments.saveAfterMediaUpload}
                  />
                </div>
              </Content>
              {showScrollIndicator && (
                <div className={styles.scrollIndicator}>
                  <button
                    onClick={handleScrollClick}
                    className={styles.scrollIndicatorButton}
                  >
                    <ArrowDown size={20} />
                  </button>
                </div>
              )}
            </div>
            <Divider type="vertical" style={{ height: "100%" }} />
            {!isDownloading && mode !== "bulkAttach" && (
              <Sider width="35%" className={styles.commentsSider}>
                <ScriptComments
                  comments={script?.comments || []}
                  highlightId={conceptComments.selectedThreadId}
                  scriptId={scriptId}
                  quotedText={conceptComments.quotedText}
                  pendingThreadId={conceptComments.pendingThreadId}
                  onClearQuotedText={conceptComments.clearQuotedText}
                  onSelectThread={conceptComments.selectCommentThread}
                  onCommentCreated={conceptComments.handleCommentCreated}
                  onGetQuote={conceptComments.getThreadQuote}
                />
              </Sider>
            )}
          </Layout>
          {mode === "bulkAttach" ? (
            <ScriptBulkAttachFooter
              attachCount={attachCount}
              isAttaching={isAttaching}
              // onUploadScript={() => setIsUploadModalShown(true)}
              onAttach={() => onAttach?.()}
            />
          ) : (
            <ScriptFooter
              serverScript={serverScript}
              briefId={briefId}
              onDownloadPdf={handleDownloadPdf}
              onRefetch={reFetchData}
              onSubmitScript={handleUploadScript}
              // onUploadScript={() => setIsUploadModalShown(true)}
              // isUploading={isUploading}
            />
          )}
        </>
      )}
      <Modal
        open={isUploadModalShown}
        onCancel={() => setIsUploadModalShown(false)}
        footer={null}
        title="Upload Script"
      >
        <UploadScriptModal
          scriptId={scriptId || ""}
          isUploading={isUploadingScript}
          uploadScripting={(url) =>
            uploadScripting({ scriptId: scriptId || "", fileUrl: url })
          }
        />
      </Modal>
    </Layout>
  );
}
